import { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, ClipboardCopy, Loader2, MapPin, ShieldCheck, Wifi, WifiOff } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

const API_BASE = import.meta.env.VITE_SUPABASE_URL || 'https://beta.crm-api.primetecia.com.br/api';

export interface AvisoConvergencia {
  id: string;
  metadados?: Record<string, unknown>;
}

interface Props {
  avisos: AvisoConvergencia[];
}

const registro = (valor: unknown): Record<string, unknown> =>
  valor && typeof valor === 'object' && !Array.isArray(valor) ? valor as Record<string, unknown> : {};

function dadosEndereco(meta: Record<string, unknown>) {
  const receita = registro(meta.receita);
  const est = registro(receita.estabelecimento || receita);
  const cidadeObj = registro(est.cidade);
  const estadoObj = registro(est.estado);
  const logradouro = [est.tipo_logradouro, est.logradouro].filter(Boolean).join(' ').trim();
  const numero = String(est.numero || '').trim();
  const convergencia = registro(meta.convergencia);
  const linha = [logradouro, numero].filter(Boolean).join(', ') || String(meta.endereco || '') || 'Endereço não informado';
  const cidade = [est.bairro, cidadeObj.nome || est.municipio, estadoObj.sigla || est.uf].filter(Boolean).join(' · ');
  const cep = String(est.cep || convergencia.cep || '').replace(/\D/g, '').replace(/^(\d{5})(\d{3})$/, '$1-$2');
  return { linha, cidade, cep, numero };
}

export function AvisosConvergenciaAgrupado({ avisos }: Props) {
  const queryClient = useQueryClient();
  const [aberto, setAberto] = useState(true);
  const [indice, setIndice] = useState(0);
  const [resultado, setResultado] = useState<'pune' | 'nao_pune' | null>(null);
  const [ciente, setCiente] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [resolvidos, setResolvidos] = useState<Set<string>>(new Set());
  const pendentes = useMemo(() => avisos.filter((a) => !resolvidos.has(a.id)), [avisos, resolvidos]);
  const atual = pendentes[Math.min(indice, Math.max(0, pendentes.length - 1))];

  if (!pendentes.length || !atual) return null;
  const meta = atual.metadados || {};
  const convergencia = registro(meta.convergencia);
  const endereco = dadosEndereco(meta);
  const provaUrls: string[] = Array.isArray(convergencia.prova_urls)
    ? convergencia.prova_urls.map(String)
    : convergencia.prova_url ? [String(convergencia.prova_url)] : [];

  const copiar = async (texto: string, rotulo: string) => {
    await navigator.clipboard.writeText(texto);
    toast.success(`${rotulo} copiado`);
  };

  const navegar = (novo: number) => {
    setIndice(Math.max(0, Math.min(novo, pendentes.length - 1)));
    setResultado(null);
    setCiente(false);
  };

  const confirmar = async () => {
    if (!resultado || !ciente || !meta.reanalise_id) return;
    setSalvando(true);
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      const response = await fetch(`${API_BASE}/v1/convergencia/reanalises/${meta.reanalise_id}/decidir`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ resultado, termo_confirmado: true }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json.error || 'A decisão não foi registrada. Atualize e tente novamente.');
      setResolvidos((prev) => new Set(prev).add(atual.id));
      setResultado(null);
      setCiente(false);
      setIndice(0);
      queryClient.invalidateQueries({ queryKey: ['comissao-perdida-convergencia'] });
      toast.success(resultado === 'pune' ? 'Cobertura confirmada — permanece punindo' : 'Inviabilidade confirmada — deixou de punir');
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'A decisão não foi registrada. Atualize e tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  if (!aberto) {
    return (
      <button type="button" onClick={() => setAberto(true)}
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-xl">
        <AlertTriangle className="h-4 w-4" /> Convergências para reconferir
        <span className="rounded-full bg-background/20 px-1.5 tabular-nums">{pendentes.length}</span>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/55 p-4">
      <section className="w-full max-w-[680px] overflow-hidden rounded-lg border border-border border-l-[3px] border-l-brand-borgonha bg-card shadow-2xl">
        <header className="flex h-12 items-center gap-2 border-b px-4">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-sm font-bold text-foreground">Reconferência de convergência</h2>
            <p className="truncate text-[11px] text-muted-foreground">CRM indica cobertura · consultor alegou inviabilidade · permanece negativa até a decisão</p>
          </div>
          <span className="text-xs font-bold tabular-nums text-primary">{Math.min(indice + 1, pendentes.length)}/{pendentes.length}</span>
          <button type="button" onClick={() => setAberto(false)} className="rounded px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted">Depois</button>
        </header>

        <div className="space-y-3 p-4">
          <div className="flex min-w-0 items-center gap-2 text-sm">
            <strong className="truncate text-foreground">{String(meta.razao_social || meta.cnpj || 'Cliente')}</strong>
            <span className="shrink-0 text-xs text-muted-foreground">CNPJ {String(meta.cnpj || '—')}</span>
            <span className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground">Pedido #{String(meta.pedido_numero || '—')}</span>
          </div>
          <div className="grid grid-cols-3 gap-x-4 text-xs">
            <p className="truncate"><span className="text-muted-foreground">Equipe:</span> <strong>{String(meta.equipe || '—')}</strong></p>
            <p className="truncate"><span className="text-muted-foreground">Consultor:</span> <strong>{String(meta.consultor || '—')}</strong></p>
            <p className="truncate"><span className="text-muted-foreground">BKO:</span> <strong>{String(meta.responsavel_nome || 'Você')}</strong></p>
          </div>

          <div className="rounded-md border border-primary/25 bg-muted/35 p-3">
            <p className="mb-1 flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> Endereço para consultar</p>
            <p className="text-base font-bold text-primary">{endereco.linha}</p>
            {endereco.cidade && <p className="text-sm text-foreground">{endereco.cidade}</p>}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <strong className="text-sm tabular-nums text-primary">CEP {endereco.cep || '—'}</strong>
              <button type="button" disabled={!endereco.cep} onClick={() => copiar(endereco.cep.replace(/\D/g, ''), 'CEP')} className="inline-flex h-6 items-center gap-1 rounded border px-2 text-[11px] text-foreground hover:bg-background disabled:opacity-40"><ClipboardCopy className="h-3 w-3" /> Copiar CEP</button>
              <button type="button" onClick={() => copiar([endereco.linha, endereco.cidade, endereco.cep].filter(Boolean).join(' · '), 'Endereço')} className="inline-flex h-6 items-center gap-1 rounded border px-2 text-[11px] text-foreground hover:bg-background"><ClipboardCopy className="h-3 w-3" /> Copiar endereço</button>
            </div>
          </div>

          <div className="grid grid-cols-[1fr_auto] items-start gap-3 text-xs">
            <div className="min-w-0 space-y-1">
              <p className="truncate"><span className="text-muted-foreground">Consultor:</span> {String(convergencia.motivo || 'Sem justificativa registrada')}</p>
              <p className="truncate"><span className="text-muted-foreground">Supervisor:</span> {String(convergencia.justificativa_supervisor || 'Sem justificativa registrada')}</p>
            </div>
            {provaUrls.length > 0 && <a href={provaUrls[0]} target="_blank" rel="noreferrer" className="text-xs font-semibold text-primary underline">Ver evidência ({provaUrls.length})</a>}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => { setResultado('pune'); setCiente(false); }} className={cn('min-h-14 rounded-md border px-3 text-sm font-bold transition-colors', resultado === 'pune' ? 'border-danger bg-danger text-danger-foreground' : 'border-danger/40 text-danger hover:bg-danger/10')}>
              <Wifi className="mx-auto mb-1 h-4 w-4" /> TEM COBERTURA — PUNE
            </button>
            <button type="button" onClick={() => { setResultado('nao_pune'); setCiente(false); }} className={cn('min-h-14 rounded-md border px-3 text-sm font-bold transition-colors', resultado === 'nao_pune' ? 'border-success bg-success text-success-foreground' : 'border-success/40 text-success hover:bg-success/10')}>
              <WifiOff className="mx-auto mb-1 h-4 w-4" /> NÃO TEM VIABILIDADE — NÃO PUNE
            </button>
          </div>

          {resultado && (
            <label className="flex cursor-pointer items-start gap-2 rounded-md border bg-muted/30 p-2.5 text-xs leading-snug text-foreground">
              <input type="checkbox" checked={ciente} onChange={(e) => setCiente(e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary" />
              <span>Consultei este endereço no sistema oficial e estou ciente de que esta decisão crítica ficará registrada para auditoria e poderá gerar medida administrativa se for irregular.</span>
            </label>
          )}
        </div>

        <footer className="flex h-12 items-center gap-2 border-t px-4">
          <button type="button" disabled={indice === 0} onClick={() => navegar(indice - 1)} className="grid h-7 w-7 place-items-center rounded border disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
          <button type="button" disabled={indice >= pendentes.length - 1} onClick={() => navegar(indice + 1)} className="grid h-7 w-7 place-items-center rounded border disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
          <span className="text-[11px] text-muted-foreground">A decisão é individual. Não existe confirmar todas.</span>
          <button type="button" disabled={!resultado || !ciente || salvando} onClick={confirmar} className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-4 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40">
            {salvando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Confirmar decisão
          </button>
        </footer>
      </section>
    </div>
  );
}
